import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { UnitMeasureDisplayComponent } from 'app/shared/components/unit-measure-display/unit-measure-display.component';

/**
 * Quantity Comparison Cell Renderer Component
 *
 * Muestra la cantidad "Después" usando UnitMeasureDisplayComponent.
 * Incluye un ícono de cambio si la cantidad difiere de "Antes".
 *
 * Uso en ColDef:
 * ```typescript
 * {
 *   field: "soldQuantityAfter",
 *   headerName: "Cantidad Después",
 *   cellRenderer: QuantityComparisonUnitCellRendererComponent,
 *   cellRendererParams: {
 *     soldBeforeField: 'soldQuantityBefore',
 *     baseBeforeField: 'baseQuantityBefore',
 *     parallelBeforeField: 'pararellQuantityBefore',
 *     soldAfterField: 'soldQuantityAfter',
 *     baseAfterField: 'baseQuantityAfter',
 *     parallelAfterField: 'pararellQuantityAfter',
 *     soldUnitBeforeField: 'soldUnitOfMeasureBefore',
 *     baseUnitBeforeField: 'baseUnitOfMeasureBefore',
 *     parallelUnitBeforeField: 'pararellUnitOfMeasureBefore',
 *     soldUnitAfterField: 'soldUnitOfMeasureAfter',
 *     baseUnitAfterField: 'baseUnitOfMeasureAfter',
 *     parallelUnitAfterField: 'pararellUnitOfMeasureAfter',
 *     getUMPActive: () => this.UMPActive,
 *   },
 * }
 * ```
 */
@Component({
  selector: 'app-quantity-comparison-unit-cell-renderer',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, UnitMeasureDisplayComponent],
  template: `
    <div class="flex items-center gap-2">
      <!-- Display "Después" usando UnitMeasureDisplayComponent -->
      <app-unit-measure-display
        [usedUnit]="{
          quantity: afterUsedQuantity,
          unitId: afterUsedUnit?._id,
          unitName: afterUsedUnit?.name,
          unitAbbreviation: afterUsedUnit?.code
        }"
        [baseUnit]="{
          quantity: afterBaseQuantity,
          unitId: afterBaseUnit?._id,
          unitName: afterBaseUnit?.name,
          unitAbbreviation: afterBaseUnit?.code
        }"
        [parallelUnit]="{
          quantity: afterParallelQuantity,
          unitId: afterParallelUnit?._id,
          unitName: afterParallelUnit?.name,
          unitAbbreviation: afterParallelUnit?.code
        }"
        [material]="{
          _id: material?._id,
          code: material?.code,
          UMPActive: material?.UMPActive ?? false
        }"
        [companyUMPActive]="isUMPActive"
      ></app-unit-measure-display>

      <!-- Ícono de cambio (solo si cambió) -->
      @if (hasChanged) {
        <mat-icon
          class="text-orange-500"
          [matTooltip]="changeTooltip"
          style="font-size: 16px; width: 16px; height: 16px;">
          change_circle
        </mat-icon>
      }
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      align-items: center;
    }
  `]
})
export class QuantityComparisonUnitCellRendererComponent<T> implements CellRenderer<T, number> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<T, number>;

  // ============================================
  // AFTER (Después) - Cantidades
  // ============================================

  get afterUsedQuantity(): number {
    const field = this.params?.colDef?.cellRendererParams?.soldAfterField as string;
    return this.params?.data?.[field] ?? 0;
  }

  get afterBaseQuantity(): number {
    const field = this.params?.colDef?.cellRendererParams?.baseAfterField as string;
    return this.params?.data?.[field] ?? 0;
  }

  get afterParallelQuantity(): number {
    const field = this.params?.colDef?.cellRendererParams?.parallelAfterField as string;
    return this.params?.data?.[field] ?? 0;
  }

  // ============================================
  // AFTER (Después) - Unidades de Medida
  // ============================================

  get afterUsedUnit(): any {
    const field = this.params?.colDef?.cellRendererParams?.soldUnitAfterField as string;
    return this.params?.data?.[field];
  }

  get afterBaseUnit(): any {
    const field = this.params?.colDef?.cellRendererParams?.baseUnitAfterField as string;
    return this.params?.data?.[field];
  }

  get afterParallelUnit(): any {
    const field = this.params?.colDef?.cellRendererParams?.parallelUnitAfterField as string;
    return this.params?.data?.[field];
  }

  // ============================================
  // BEFORE (Antes) - Cantidades
  // ============================================

  get beforeUsedQuantity(): number {
    const field = this.params?.colDef?.cellRendererParams?.soldBeforeField as string;
    return this.params?.data?.[field] ?? 0;
  }

  get beforeBaseQuantity(): number {
    const field = this.params?.colDef?.cellRendererParams?.baseBeforeField as string;
    return this.params?.data?.[field] ?? 0;
  }

  get beforeParallelQuantity(): number {
    const field = this.params?.colDef?.cellRendererParams?.parallelBeforeField as string;
    return this.params?.data?.[field] ?? 0;
  }

  // ============================================
  // BEFORE (Antes) - Unidades de Medida
  // ============================================

  get beforeUsedUnit(): any {
    const field = this.params?.colDef?.cellRendererParams?.soldUnitBeforeField as string;
    return this.params?.data?.[field];
  }

  get beforeBaseUnit(): any {
    const field = this.params?.colDef?.cellRendererParams?.baseUnitBeforeField as string;
    return this.params?.data?.[field];
  }

  get beforeParallelUnit(): any {
    const field = this.params?.colDef?.cellRendererParams?.parallelUnitBeforeField as string;
    return this.params?.data?.[field];
  }

  // ============================================
  // Material y Flags
  // ============================================

  get material(): any {
    return (this.params?.data as any)?.material;
  }

  get isUMPActive(): boolean {
    const getUMPActive = this.params?.colDef?.cellRendererParams?.getUMPActive as (() => boolean) | undefined;
    return getUMPActive ? getUMPActive() : false;
  }

  // ============================================
  // Change Detection
  // ============================================

  get hasChanged(): boolean {
    // Comparar cantidad vendida (sold quantity)
    const soldChanged = Math.abs(this.beforeUsedQuantity - this.afterUsedQuantity) > 0.001;

    // Comparar cantidad base (base quantity)
    const baseChanged = Math.abs(this.beforeBaseQuantity - this.afterBaseQuantity) > 0.001;

    // Comparar cantidad paralela (parallel quantity)
    const parallelChanged = Math.abs(this.beforeParallelQuantity - this.afterParallelQuantity) > 0.001;

    return soldChanged || baseChanged || parallelChanged;
  }

  get changeTooltip(): string {
    let tooltip = 'Cantidad modificada:\n';

    // Sold quantity
    if (Math.abs(this.beforeUsedQuantity - this.afterUsedQuantity) > 0.001) {
      const unitName = this.afterUsedUnit?.name ?? '-';
      tooltip += `Vendida: ${this.beforeUsedQuantity} → ${this.afterUsedQuantity} (${unitName})\n`;
    }

    // Base quantity
    if (Math.abs(this.beforeBaseQuantity - this.afterBaseQuantity) > 0.001) {
      const unitName = this.afterBaseUnit?.name ?? '-';
      tooltip += `UMB: ${this.beforeBaseQuantity} → ${this.afterBaseQuantity} (${unitName})\n`;
    }

    // Parallel quantity
    if (Math.abs(this.beforeParallelQuantity - this.afterParallelQuantity) > 0.001) {
      const unitName = this.afterParallelUnit?.name ?? '-';
      tooltip += `UMP: ${this.beforeParallelQuantity} → ${this.afterParallelQuantity} (${unitName})`;
    }

    return tooltip.trim();
  }
}
